/**
 * Route table for demo mode.
 *
 * Each entry maps a method + path pattern to a handler that reads and writes
 * the in-memory store. Paths are matched against the request URL with the
 * `API_ROOT` prefix already stripped.
 *
 * Anything not listed here is refused by the interceptor rather than forwarded
 * — see the fail-closed branch in `index.ts`.
 */

import { buildTiersWithAvailability, spotsTakenForSubscription } from "./availability";
import type {
  DemoHarvestLog,
  DemoHarvestProduct,
  DemoOrder,
  DemoPickup,
  DemoPickupLocation,
  DemoSubscription,
  DemoTier,
  DemoUser,
} from "./fixtures";
import { nextDemoId } from "./ids";
import {
  toHarvestLogResponse,
  toHarvestProductResponse,
  toOrderResponse,
  toPickupLocationResponse,
  toPickupResponse,
  toProfilePayload,
  toSeasonResponse,
  toSubscriptionResponse,
  toTierResponse,
  toUserListItem,
} from "./serializers";
import { getDemoIdentity, type DemoRole } from "./session";
import {
  findUser,
  findUserByCompanyRegistrationNumber,
  getData,
  subscriberCountByTierId,
  userHasPickup,
} from "./store";

export interface HandlerContext {
  params: Record<string, string>;
  body: Record<string, unknown>;
  query: URLSearchParams;
  role: DemoRole;
}

export interface HandlerResult {
  status: number;
  data: unknown;
}

type Handler = (ctx: HandlerContext) => HandlerResult;

const ok = (data: unknown): HandlerResult => ({ status: 200, data });
const created = (data: unknown): HandlerResult => ({ status: 201, data });
const notFound = (message: string): HandlerResult => ({
  status: 404,
  data: { message },
});
const badRequest = (message: string): HandlerResult => ({
  status: 400,
  data: { message },
});
const forbidden = (message: string): HandlerResult => ({
  status: 403,
  data: { message },
});

/** Frozen timestamp so a demo session never shows a "just now" that drifts. */
function stamp(): string {
  return new Date().toISOString();
}

function currentUser(role: DemoRole): DemoUser | undefined {
  return findUser(getDemoIdentity(role).userId);
}

/* ------------------------------- auth ------------------------------- */

const getProfile: Handler = ({ role }) => {
  const user = currentUser(role);
  if (!user) return notFound("User not found");
  return ok(toProfilePayload(user, userHasPickup(user._id)));
};

const patchProfile: Handler = ({ role, body }) => {
  const user = currentUser(role);
  if (!user) return notFound("User not found");

  const editable = [
    "firstName",
    "secondName",
    "surname",
    "phoneNumber",
    "companyName",
    "companyRegistrationNumber",
    "address",
    "postalCode",
    "city",
    "shippingHeavyPrice",
    "shippingLightPrice",
  ] as const;

  for (const key of editable) {
    if (key in body) {
      (user as unknown as Record<string, unknown>)[key] = body[key];
    }
  }
  return ok(toProfilePayload(user, userHasPickup(user._id)));
};

const deleteOwnAccount: Handler = () =>
  forbidden("Account deletion is disabled in demo mode");

const listUsers: Handler = () =>
  ok(getData().users.map((u) => toUserListItem(u, userHasPickup(u._id))));

const createUser: Handler = ({ body }) => {
  const data = getData();
  const email = String(body.email ?? "").trim().toLowerCase();
  if (!email) return badRequest("Email is required");
  if (data.users.some((u) => u.email.toLowerCase() === email)) {
    return badRequest("A user with that email already exists");
  }

  const user: DemoUser = {
    _id: nextDemoId(),
    firstName: String(body.firstName ?? ""),
    secondName: body.secondName ? String(body.secondName) : undefined,
    surname: String(body.surname ?? ""),
    email,
    role: String(body.role ?? "social"),
    phoneNumber: body.phoneNumber ? String(body.phoneNumber) : undefined,
    emailVerified: true,
    createdAt: stamp(),
    companyName: body.companyName ? String(body.companyName) : undefined,
    companyRegistrationNumber: body.companyRegistrationNumber
      ? String(body.companyRegistrationNumber)
      : undefined,
    address: body.address ? String(body.address) : undefined,
    postalCode: body.postalCode ? String(body.postalCode) : undefined,
    city: body.city ? String(body.city) : undefined,
    subscriptionHistory: [],
  };
  data.users.push(user);
  return created({ message: "User created successfully", userId: user._id });
};

const deleteUser: Handler = ({ params }) => {
  const data = getData();
  const index = data.users.findIndex((u) => u._id === params.userId);
  if (index === -1) return notFound("User not found");
  data.users.splice(index, 1);
  data.pickups = data.pickups.filter((p) => p.userId !== params.userId);
  return ok({ message: "Account deleted" });
};

const assignSubscription: Handler = ({ params, body }) => {
  const data = getData();
  const user = findUser(params.userId);
  if (!user) return notFound("User not found");
  if (userHasPickup(user._id)) {
    return badRequest("User already has a CSA pickup");
  }

  const tierId = String(body.subscriptionTierId ?? "");
  const tier = data.tiers.find((t) => t._id === tierId);
  if (!tier) return badRequest("Unknown subscription tier");

  const now = stamp();
  const pickup: DemoPickup = {
    _id: nextDemoId(),
    userId: user._id,
    pickupLocationId: String(body.pickupLocationId ?? ""),
    signupPickupLocationId: String(body.pickupLocationId ?? ""),
    pickupDay: String(body.pickupDay ?? ""),
    subscriptionTierId: tierId,
    subscriptionPaymentStatus:
      (body.subscriptionPaymentStatus as DemoPickup["subscriptionPaymentStatus"]) ??
      "pending",
    friendPickupFirstName: (body.friendPickupFirstName as string | null) ?? null,
    friendPickupLastName: (body.friendPickupLastName as string | null) ?? null,
    friendPickupPhone: (body.friendPickupPhone as string | null) ?? null,
    createdAt: now,
    updatedAt: now,
  };
  data.pickups.push(pickup);

  user.subscriptionHistory.push({
    subscriptionTierId: tier._id,
    subscriptionTierName: tier.name,
    year: new Date().getFullYear(),
  });

  return ok({ message: "Subscription assigned", hasCSASubscription: true });
};

const removeSubscription: Handler = ({ params }) => {
  const data = getData();
  const user = findUser(params.userId);
  if (!user) return notFound("User not found");
  data.pickups = data.pickups.filter((p) => p.userId !== params.userId);
  return ok({ message: "OK", hasCSASubscription: false });
};

const sendPaymentReminder: Handler = ({ params }) => {
  const user = findUser(params.userId);
  if (!user) return notFound("User not found");
  // No mail is sent in demo mode; the dashboard only needs the acknowledgement.
  return ok({
    message: "Payment reminder queued (no email is sent in demo mode)",
    emailSent: false,
  });
};

/* --------------------------- pickup locations --------------------------- */

const listPickupLocations: Handler = () => {
  const { pickupLocations } = getData();
  // The backend returns 404 rather than an empty array — mirrored here so the
  // dashboard's empty state behaves the same way.
  if (pickupLocations.length === 0) return notFound("No pickup locations found");
  return ok(pickupLocations.map(toPickupLocationResponse));
};

const createPickupLocation: Handler = ({ body }) => {
  const data = getData();
  const now = stamp();
  const location: DemoPickupLocation = {
    _id: nextDemoId(),
    name: String(body.name ?? ""),
    address: String(body.address ?? ""),
    latitude: Number(body.latitude ?? 0),
    longitude: Number(body.longitude ?? 0),
    days: (body.days as DemoPickupLocation["days"]) ?? [],
    createdAt: now,
    updatedAt: now,
  };
  data.pickupLocations.unshift(location);
  return created(toPickupLocationResponse(location));
};

const updatePickupLocation: Handler = ({ params, body }) => {
  const location = getData().pickupLocations.find((l) => l._id === params.id);
  if (!location) return notFound("No pickup location found");
  Object.assign(location, body, { updatedAt: stamp() });
  return ok(toPickupLocationResponse(location));
};

const deletePickupLocation: Handler = ({ params }) => {
  const data = getData();
  const index = data.pickupLocations.findIndex((l) => l._id === params.id);
  if (index === -1) return notFound("No pickup location found");
  if (data.pickupLocations[index].undeletable) {
    return forbidden("This pickup location cannot be deleted");
  }
  data.pickupLocations.splice(index, 1);
  return ok({ message: "Pickup location deleted successfully" });
};

/* ------------------------------- pickups ------------------------------- */

const listPickups: Handler = ({ role }) => {
  const data = getData();
  const identity = getDemoIdentity(role);
  // Staff see every pickup; a member only sees their own.
  const visible =
    role === "admin" || role === "employee"
      ? data.pickups
      : data.pickups.filter((p) => p.userId === identity.userId);
  return ok(visible.map((p) => toPickupResponse(p, findUser(p.userId))));
};

const getPickup: Handler = ({ params }) => {
  const pickup = getData().pickups.find(
    (p) => p._id === params.id || p.userId === params.id,
  );
  if (!pickup) return notFound("Pickup not found");
  return ok(toPickupResponse(pickup, findUser(pickup.userId)));
};

const createPickup: Handler = ({ body }) => {
  const data = getData();
  const now = stamp();
  const pickup: DemoPickup = {
    _id: nextDemoId(),
    userId: String(body.userId ?? ""),
    pickupLocationId: String(body.pickupLocationId ?? ""),
    signupPickupLocationId: String(body.pickupLocationId ?? ""),
    pickupDay: String(body.pickupDay ?? ""),
    subscriptionTierId: String(body.subscriptionTierId ?? ""),
    subscriptionPaymentStatus:
      (body.subscriptionPaymentStatus as DemoPickup["subscriptionPaymentStatus"]) ??
      "pending",
    friendPickupFirstName: (body.friendPickupFirstName as string | null) ?? null,
    friendPickupLastName: (body.friendPickupLastName as string | null) ?? null,
    friendPickupPhone: (body.friendPickupPhone as string | null) ?? null,
    createdAt: now,
    updatedAt: now,
  };
  data.pickups.push(pickup);
  return created(toPickupResponse(pickup, findUser(pickup.userId)));
};

const updatePickup: Handler = ({ params, body }) => {
  const pickup = getData().pickups.find((p) => p._id === params.id);
  if (!pickup) return notFound("Pickup not found");
  Object.assign(pickup, body, { updatedAt: stamp() });
  if (pickup.subscriptionPaymentStatus === "paid" && !pickup.paidAt) {
    pickup.paidAt = stamp();
  }
  return ok(toPickupResponse(pickup, findUser(pickup.userId)));
};

const deletePickup: Handler = ({ params }) => {
  const data = getData();
  const index = data.pickups.findIndex((p) => p._id === params.id);
  if (index === -1) return notFound("Pickup not found");
  data.pickups.splice(index, 1);
  return ok({ message: "Pickup deleted successfully" });
};

/* -------------------------------- tiers -------------------------------- */

const listTiers: Handler = ({ query }) => {
  const subscriptionId = query.get("subscriptionId");
  const tiers = getData().tiers.filter(
    (t) => !subscriptionId || t.subscriptionId === subscriptionId,
  );
  return ok(tiers.map(toTierResponse));
};

const createTier: Handler = ({ body }) => {
  const data = getData();
  const tier: DemoTier = {
    _id: nextDemoId(),
    subscriptionId: String(body.subscriptionId ?? ""),
    name: String(body.name ?? ""),
    price: Number(body.price ?? 0),
    oldCsaMemberPrice: Number(body.oldCsaMemberPrice ?? 0),
    spots: body.spots == null ? undefined : Number(body.spots),
    dependsOn: body.dependsOn ? String(body.dependsOn) : undefined,
    takesFrom: (body.takesFrom as string[]) ?? [],
  };
  data.tiers.push(tier);
  return created(toTierResponse(tier));
};

const updateTier: Handler = ({ params, body }) => {
  const tier = getData().tiers.find((t) => t._id === params.id);
  if (!tier) return notFound("Tier not found");
  Object.assign(tier, body);
  return ok(toTierResponse(tier));
};

const deleteTier: Handler = ({ params }) => {
  const data = getData();
  const index = data.tiers.findIndex((t) => t._id === params.id);
  if (index === -1) return notFound("Tier not found");
  if (data.pickups.some((p) => p.subscriptionTierId === params.id)) {
    return badRequest("Tier has subscribers and cannot be deleted");
  }
  data.tiers.splice(index, 1);
  return ok({ message: "Tier deleted" });
};

/* ---------------------------- subscriptions ---------------------------- */

const listSubscriptions: Handler = () => {
  const data = getData();
  const counts = subscriberCountByTierId();
  const tiersWithAvailability = buildTiersWithAvailability(
    data.tiers,
    data.subscriptions,
    counts,
  );

  return ok(
    data.subscriptions.map((sub) => ({
      ...toSubscriptionResponse(sub),
      tiers: tiersWithAvailability.filter((t) => t.subscriptionId === sub._id),
      spotsTaken: spotsTakenForSubscription(sub._id, data.tiers, counts),
    })),
  );
};

const createSubscription: Handler = ({ body }) => {
  const data = getData();
  const subscription: DemoSubscription = {
    _id: nextDemoId(),
    name: String(body.name ?? ""),
    spots: body.spots == null ? undefined : Number(body.spots),
    isCombo: Boolean(body.isCombo),
    isActive: Boolean(body.isActive),
  };
  data.subscriptions.push(subscription);
  return created({ ...toSubscriptionResponse(subscription), tiers: [] });
};

const updateSubscription: Handler = ({ params, body }) => {
  const data = getData();
  const subscription = data.subscriptions.find((s) => s._id === params.id);
  if (!subscription) return notFound("Subscription not found");
  Object.assign(subscription, body);

  const counts = subscriberCountByTierId();
  const tiers = buildTiersWithAvailability(
    data.tiers,
    data.subscriptions,
    counts,
  ).filter((t) => t.subscriptionId === subscription._id);
  return ok({ ...toSubscriptionResponse(subscription), tiers });
};

const deleteSubscription: Handler = ({ params }) => {
  const data = getData();
  const index = data.subscriptions.findIndex((s) => s._id === params.id);
  if (index === -1) return notFound("Subscription not found");
  if (data.tiers.some((t) => t.subscriptionId === params.id)) {
    return badRequest("Subscription still has tiers and cannot be deleted");
  }
  data.subscriptions.splice(index, 1);
  return ok({ message: "Subscription deleted" });
};

/* --------------------------- harvest products --------------------------- */

const listHarvestProducts: Handler = () =>
  ok(getData().harvestProducts.map(toHarvestProductResponse));

const createHarvestProduct: Handler = ({ body }) => {
  const data = getData();
  const now = stamp();
  const product: DemoHarvestProduct = {
    _id: nextDemoId(),
    name: String(body.name ?? ""),
    quantity: Number(body.quantity ?? 0),
    price: Number(body.price ?? 0),
    discount: body.discount == null ? null : Number(body.discount),
    createdAt: now,
    updatedAt: now,
  };
  data.harvestProducts.unshift(product);
  return created(toHarvestProductResponse(product));
};

const updateHarvestProduct: Handler = ({ params, body }) => {
  const product = getData().harvestProducts.find((p) => p._id === params.id);
  if (!product) return notFound("Harvest product not found");
  Object.assign(product, body, { updatedAt: stamp() });
  return ok(toHarvestProductResponse(product));
};

const deleteHarvestProduct: Handler = ({ params }) => {
  const data = getData();
  const index = data.harvestProducts.findIndex((p) => p._id === params.id);
  if (index === -1) return notFound("Harvest product not found");
  data.harvestProducts.splice(index, 1);
  return ok({ message: "Harvest product deleted" });
};

/* ----------------------------- harvest logs ----------------------------- */

const listHarvestLogs: Handler = () =>
  ok(getData().harvestLogs.map(toHarvestLogResponse));

const createHarvestLog: Handler = ({ body }) => {
  const data = getData();
  const log: DemoHarvestLog = {
    _id: nextDemoId(),
    startDate: String(body.startDate ?? ""),
    endDate: String(body.endDate ?? ""),
    comment: String(body.comment ?? ""),
    products: (body.products as DemoHarvestLog["products"]) ?? [],
    createdAt: stamp(),
  };
  data.harvestLogs.unshift(log);
  return created(toHarvestLogResponse(log));
};

const deleteAllHarvestLogs: Handler = () => {
  const data = getData();
  const deletedCount = data.harvestLogs.length;
  data.harvestLogs = [];
  return ok({ message: "All harvest logs deleted", deletedCount });
};

const deleteHarvestLog: Handler = ({ params }) => {
  const data = getData();
  const index = data.harvestLogs.findIndex((l) => l._id === params.logId);
  if (index === -1) return notFound("Harvest log not found");
  data.harvestLogs.splice(index, 1);
  return ok({ message: "Harvest log deleted" });
};

/* -------------------------------- season -------------------------------- */

const getSeason: Handler = () => ok(toSeasonResponse(getData().season));

const updateSeason: Handler = ({ body }) => {
  const data = getData();
  Object.assign(data.season, body, { updatedAt: stamp() });
  return ok(toSeasonResponse(data.season));
};

const joinSeasonWaitlist: Handler = ({ role }) => {
  const data = getData();
  const { userId } = getDemoIdentity(role);
  const alreadyOnWaitlist = data.season.waitlist.includes(userId);
  if (!alreadyOnWaitlist) data.season.waitlist.push(userId);
  return ok({
    waitlist: data.season.waitlist,
    alreadyOnWaitlist,
    message: alreadyOnWaitlist ? "Already on the waitlist" : "Added to waitlist",
  });
};

/* -------------------------------- orders -------------------------------- */

function visibleOrders(role: DemoRole): DemoOrder[] {
  const data = getData();
  if (role === "admin" || role === "employee") return data.orders;
  const user = currentUser(role);
  if (!user?.companyRegistrationNumber) return [];
  return data.orders.filter(
    (o) => o.companyRegistrationNumber === user.companyRegistrationNumber,
  );
}

const listOrders: Handler = ({ role }) =>
  ok(
    visibleOrders(role).map((o) =>
      toOrderResponse(o, findUserByCompanyRegistrationNumber(o.companyRegistrationNumber)),
    ),
  );

const getOrder: Handler = ({ params, role }) => {
  const order = visibleOrders(role).find((o) => o._id === params.id);
  if (!order) return notFound("Order not found");
  return ok(
    toOrderResponse(
      order,
      findUserByCompanyRegistrationNumber(order.companyRegistrationNumber),
    ),
  );
};

const createOrder: Handler = ({ body, role }) => {
  const data = getData();
  const user = currentUser(role);
  const now = stamp();
  const products = (body.products as DemoOrder["products"]) ?? [];
  const order: DemoOrder = {
    _id: nextDemoId(),
    companyRegistrationNumber:
      String(body.companyRegistrationNumber ?? user?.companyRegistrationNumber ?? ""),
    totalPrice: Number(body.totalPrice ?? 0),
    deliveryMethod: (body.deliveryMethod as DemoOrder["deliveryMethod"]) ?? "pickup",
    companyAddress: body.companyAddress ? String(body.companyAddress) : undefined,
    companyPostalCode: body.companyPostalCode ? String(body.companyPostalCode) : undefined,
    companyCity: body.companyCity ? String(body.companyCity) : undefined,
    shippingPrice: body.shippingPrice == null ? undefined : Number(body.shippingPrice),
    status: "Pending",
    products,
    createdAt: now,
    updatedAt: now,
  };
  data.orders.unshift(order);

  // Mirror the backend's stock deduction so the harvest table stays consistent.
  for (const line of products) {
    const product = data.harvestProducts.find((p) => p.name === line.productName);
    if (product) product.quantity = Math.max(0, product.quantity - line.quantity);
  }

  return created(
    toOrderResponse(order, findUserByCompanyRegistrationNumber(order.companyRegistrationNumber)),
  );
};

const updateOrder: Handler = ({ params, body }) => {
  const data = getData();
  const order = data.orders.find((o) => o._id === params.id);
  if (!order) return notFound("Order not found");

  const previousStatus = order.status;
  Object.assign(order, body, { updatedAt: stamp() });

  // Cancelling returns stock, matching the backend's stockRestored behaviour.
  if (order.status === "Cancelled" && previousStatus !== "Cancelled") {
    for (const line of order.products) {
      const product = data.harvestProducts.find((p) => p.name === line.productName);
      if (product) product.quantity += line.quantity;
    }
  }

  return ok(
    toOrderResponse(order, findUserByCompanyRegistrationNumber(order.companyRegistrationNumber)),
  );
};

/* --------------------------- event ticket admin --------------------------- */

function ticketsSold(event: { purchases: { quantity: number }[] }): number {
  return event.purchases.reduce((total, p) => total + p.quantity, 0);
}

const listAdminEvents: Handler = () =>
  ok(
    getData().events.map((event) => ({
      _id: event._id,
      eventName: event.eventName,
      eventStartTime: event.eventStartTime,
      eventEndTime: event.eventEndTime,
      locationOfEvent: event.locationOfEvent,
      numberOfAvailableTickets: event.numberOfAvailableTickets,
      ticketsSold: ticketsSold(event),
      purchaseCount: event.purchases.length,
    })),
  );

const getAdminEventDetails: Handler = ({ params }) => {
  const event = getData().events.find((e) => e._id === params.eventId);
  if (!event) return notFound("Event not found");
  return ok({
    event: {
      _id: event._id,
      eventName: event.eventName,
      eventStartTime: event.eventStartTime,
      eventEndTime: event.eventEndTime,
      locationOfEvent: event.locationOfEvent,
      numberOfAvailableTickets: event.numberOfAvailableTickets,
    },
    ticketsSold: ticketsSold(event),
    purchaseCount: event.purchases.length,
    purchases: event.purchases,
  });
};

/* ---------------------------- contributions ---------------------------- */

/**
 * The public contribution reads pass through to the live API (see PASSTHROUGH
 * in index.ts), but this one is authenticated and would 401 on the demo token.
 * An empty list is the honest answer: the demo dataset has no comments.
 */
const listContributionComments: Handler = () => ok([]);

/* ------------------------------ route table ------------------------------ */

interface Route {
  method: string;
  pattern: string;
  handler: Handler;
}

const ROUTES: Route[] = [
  { method: "GET", pattern: "/auth/profile", handler: getProfile },
  { method: "PATCH", pattern: "/auth/profile", handler: patchProfile },
  { method: "DELETE", pattern: "/auth/profile", handler: deleteOwnAccount },
  { method: "GET", pattern: "/auth/users", handler: listUsers },
  { method: "POST", pattern: "/auth/users", handler: createUser },
  { method: "DELETE", pattern: "/auth/users/:userId", handler: deleteUser },
  {
    method: "POST",
    pattern: "/auth/users/:userId/assign-subscription",
    handler: assignSubscription,
  },
  {
    method: "POST",
    pattern: "/auth/users/:userId/remove-subscription",
    handler: removeSubscription,
  },
  {
    method: "POST",
    pattern: "/auth/users/:userId/send-csa-payment-reminder",
    handler: sendPaymentReminder,
  },

  { method: "GET", pattern: "/pickup-locations", handler: listPickupLocations },
  { method: "POST", pattern: "/pickup-locations", handler: createPickupLocation },
  { method: "PUT", pattern: "/pickup-locations/:id", handler: updatePickupLocation },
  { method: "DELETE", pattern: "/pickup-locations/:id", handler: deletePickupLocation },

  { method: "GET", pattern: "/pickups", handler: listPickups },
  { method: "POST", pattern: "/pickups", handler: createPickup },
  { method: "GET", pattern: "/pickups/:id", handler: getPickup },
  { method: "PUT", pattern: "/pickups/:id", handler: updatePickup },
  { method: "DELETE", pattern: "/pickups/:id", handler: deletePickup },

  { method: "GET", pattern: "/tiers", handler: listTiers },
  { method: "POST", pattern: "/tiers", handler: createTier },
  { method: "PUT", pattern: "/tiers/:id", handler: updateTier },
  { method: "DELETE", pattern: "/tiers/:id", handler: deleteTier },

  { method: "GET", pattern: "/subscriptions", handler: listSubscriptions },
  { method: "POST", pattern: "/subscriptions", handler: createSubscription },
  { method: "PUT", pattern: "/subscriptions/:id", handler: updateSubscription },
  { method: "DELETE", pattern: "/subscriptions/:id", handler: deleteSubscription },

  { method: "GET", pattern: "/harvest-products", handler: listHarvestProducts },
  { method: "POST", pattern: "/harvest-products", handler: createHarvestProduct },
  { method: "PUT", pattern: "/harvest-products/:id", handler: updateHarvestProduct },
  { method: "DELETE", pattern: "/harvest-products/:id", handler: deleteHarvestProduct },

  { method: "GET", pattern: "/harvest-logs", handler: listHarvestLogs },
  { method: "POST", pattern: "/harvest-logs", handler: createHarvestLog },
  { method: "DELETE", pattern: "/harvest-logs", handler: deleteAllHarvestLogs },
  { method: "DELETE", pattern: "/harvest-logs/:logId", handler: deleteHarvestLog },

  { method: "GET", pattern: "/season", handler: getSeason },
  // Registered before /season/:id so the literal segment wins the match.
  { method: "POST", pattern: "/season/waitlist", handler: joinSeasonWaitlist },
  { method: "PUT", pattern: "/season/:id", handler: updateSeason },

  { method: "GET", pattern: "/orders", handler: listOrders },
  { method: "POST", pattern: "/orders", handler: createOrder },
  { method: "GET", pattern: "/orders/:id", handler: getOrder },
  { method: "PUT", pattern: "/orders/:id", handler: updateOrder },

  { method: "GET", pattern: "/event-tickets/admin/events", handler: listAdminEvents },
  {
    method: "GET",
    pattern: "/event-tickets/admin/events/:eventId",
    handler: getAdminEventDetails,
  },

  {
    method: "GET",
    pattern: "/contributions/:id/comments",
    handler: listContributionComments,
  },
];

function matchPattern(
  pattern: string,
  path: string,
): Record<string, string> | null {
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = path.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i += 1) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];
    if (patternPart.startsWith(":")) {
      params[patternPart.slice(1)] = decodeURIComponent(pathPart);
    } else if (patternPart !== pathPart) {
      return null;
    }
  }
  return params;
}

export function matchRoute(
  method: string,
  path: string,
): { handler: Handler; params: Record<string, string> } | null {
  for (const route of ROUTES) {
    if (route.method !== method) continue;
    const params = matchPattern(route.pattern, path);
    if (params) return { handler: route.handler, params };
  }
  return null;
}
